import React, { Suspense, lazy } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import ProtectedRoute from "./Components/ProtectedRoute";
import SessionTimeoutHandler from "./Components/SessionTimeoutHandler";
import ChunkErrorBoundary from "./Components/ChunkErrorBoundary";
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import './styles/MobileDesignSystem.css';
import { ConfigProvider } from "antd";
import { THEME_COLORS } from "./utils/designSystem";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// --- LAZY-LOADED CORE APPLICATION PAGES ---
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Userpage = lazy(() => import("./pages/Userpage"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Login = lazy(() => import("./pages/Login"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const UnifiedDashboard = lazy(() => import("./pages/Dashboard/UnifiedDashboard"));

// --- LAZY-LOADED USER & ONBOARDING MODULES ---
const UsersLayout = lazy(() => import("./pages/Users/UsersLayout"));
const UsersList = lazy(() => import("./pages/Users/UsersList"));
const InviteUser = lazy(() => import("./pages/Users/InviteUser"));
const PendingApprovals = lazy(() =>
  import("./pages/Users/PendingApprovals").catch(() =>
    import("./pages/KYCApproval")
  )
);
const InviteeOnboarding = lazy(() =>
  import("./pages/Onboarding/InviteeOnboarding").catch(() => ({
    default: () => (
      <div className="container p-5 text-center">
        <h4>Loading Onboarding Portal...</h4>
      </div>
    )
  }))
);

// --- LAZY-LOADED COMMISSIONS & PERMISSIONS ---
const CommissionsLayout = lazy(() => import("./pages/Commissions/CommissionsLayout"));
const CommissionDashboard = lazy(() => import("./pages/Commissions/CommissionDashboard"));
const MenuAccess = lazy(() => import("./pages/Permissions/MenuAccess"));
const AdminPermissionsMenuAccess = lazy(() =>
  import("./pages/Permissions/AdminPermissionsMenuAccess")
);
const AdminCommissionWithdrawals = lazy(() => import("./pages/Permissions/AdminCommissionWithdrawals"));
const AdminBbpsSettings = lazy(() => import("./pages/Permissions/AdminBbpsSettings"));

// --- LAZY-LOADED FINANCIAL & TRANSACTION MODULES ---
const PosPayment = lazy(() => import("./pages/Pos/PosPayment"));
const AdminPosApprovals = lazy(() => import("./pages/Pos/AdminPosApprovals"));
const AdminPosConfig = lazy(() => import("./pages/Pos/AdminPosConfig"));
const QrPgPayment = lazy(() => import("./pages/QrPg/QrPgPayment"));
const AdminQrPgSetup = lazy(() => import("./pages/QrPg/AdminQrPgSetup"));
const BankSettlement = lazy(() => import("./pages/Settlement/BankSettlement"));
const AdminSettlementCharges = lazy(() => import("./pages/Settlement/AdminSettlementCharges"));
const AdminAllReports = lazy(() => import("./pages/AdminReports/AdminAllReports"));
const BillPayments = lazy(() => import("./pages/BillPayments/BillPayments"));
const Reports = lazy(() => import("./pages/Reports"));
const PassBook = lazy(() => import("./pages/PassBook"));
const BankTransfer = lazy(() => import("./pages/BankTransfer"));
const Payments = lazy(() => import("./pages/Payments"));
const TransactionHistory = lazy(() => import("./pages/TransactionHistory"));
const AddNewBankAccount = lazy(() => import('./pages/AddNewBankAccount'));
const AddCreditCard = lazy(() => import("./pages/AddCreditCard"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const PaymentStatus = lazy(() => import("./pages/PaymentStatus"));
const PaymentGateWays = lazy(() => import("./pages/PaymentGateWays"));
const Payin = lazy(() => import("./pages/Payin/Payin"));
const AdminCardToBankConfig = lazy(() => import("./pages/CardToBank/AdminCardToBankConfig"));
const CardToBankPayment = lazy(() => import("./pages/CardToBank/CardToBankPayment"));
const AdminNotifications = lazy(() => import("./pages/Notifications/AdminNotifications"));
const AdminDisputeDesk = lazy(() => import("./pages/Disputes/AdminDisputeDesk"));
const MyDisputesTab = lazy(() => import("./pages/Disputes/MyDisputesTab"));

// --- LAZY-LOADED PUBLIC & STATIC WEBPAGES ---
const HomePage = lazy(() => import("./pages/Webpages/WebHomePage"));
const AboutUs = lazy(() => import("./pages/Webpages/AboutUs"));
const Features = lazy(() => import("./pages/Webpages/Features"));
const HowItWorks = lazy(() => import("./pages/Webpages/HowItWorks"));
const ContactUs = lazy(() => import("./pages/Webpages/ContactUs"));
const Footer = lazy(() => import("./pages/Webpages/WebPageFooter"));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));

// --- LAZY-LOADED AUTH UTILITIES ---
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const OTP = lazy(() => import("./pages/Email-otp"));
const PasswordSetup = lazy(() => import("./pages/PasswordSetup"));
const Register = lazy(() => import("./pages/Register"));

// --- LAZY-LOADED LEGACY SCREENS ---
const SuperDistributor = lazy(() => import("./pages/SuperDistributor"));
const GetSuperDistributor = lazy(() => import("./pages/SuperDistributor/GetSuperDistributor"));
const SuperDistributorDetails = lazy(() => import("./pages/SuperDistributor/SuperDistributorDetails"));
const AddSuperDistributor = lazy(() => import("./pages/SuperDistributor/AddSuperdistributor"));
const SuperDistributorMargin = lazy(() => import("./pages/SuperDistributor/SuperDistributorMargin"));

const Distributor = lazy(() => import("./pages/Distributor"));
const GetDistributor = lazy(() => import("./pages/Distributor/GetDistributor"));
const DistributorDetails = lazy(() => import("./pages/Distributor/DistributorDetails"));
const AddDistributor = lazy(() => import("./pages/Distributor/AddDistributor"));
const DistributorMargin = lazy(() => import("./pages/Distributor/DistributorMargin"));

const Retailer = lazy(() => import("./pages/Retailer"));
const GetRetailers = lazy(() => import("./pages/Retailer/GetRetailer"));
const RetailerDetails = lazy(() => import('./pages/Retailer/RetailerDetails'));
const AddRetailer = lazy(() => import("./pages/Retailer/AddRetailer"));
const RetailerMargin = lazy(() => import('./pages/Retailer/RetailerMargin'));
const Retail = lazy(() => import("./pages/RetailList"));

const GetReporter = lazy(() => import("./pages/Reporter/GetReporter"));
const AddReporter = lazy(() => import("./pages/Reporter/AddReporter"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Margin = lazy(() => import("./pages/Margin"));
const ReportsHub = lazy(() => import("./pages/Reports/ReportsHub"));

const PageLoader = ({ text = "Loading..." }) => (
  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "50vh", gap: "12px" }}>
    <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
      <span className="visually-hidden">Loading...</span>
    </div>
    <span style={{ fontSize: "14px", color: THEME_COLORS.textSecondary, fontWeight: 500 }}>{text}</span>
  </div>
);

const globalTheme = {
  token: {
    colorPrimary: THEME_COLORS.primary,
    colorSuccess: THEME_COLORS.success,
    colorWarning: THEME_COLORS.warning,
    colorError: THEME_COLORS.error,
    colorInfo: THEME_COLORS.info,
    borderRadius: 8,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    colorTextHeading: THEME_COLORS.textPrimary,
    colorText: THEME_COLORS.textPrimary,
    colorTextSecondary: THEME_COLORS.textSecondary,
    zIndexPopupBase: 2000
  }
};

function App() {
  return (
    <ConfigProvider theme={globalTheme}>
      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        icon={false}
        limit={4}
      />
      <SessionTimeoutHandler />
      <ChunkErrorBoundary>
        <Suspense fallback={<PageLoader text="Loading page..." />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Public self-onboarding routes for invited users */}
          <Route path="/register/onboard" element={<InviteeOnboarding />} />
          <Route path="/onboard" element={<InviteeOnboarding />} />

          <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer", "reports"]}><Dashboard /></ProtectedRoute>}>
            <Route index={true} element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer", "reports"]}><UnifiedDashboard /></ProtectedRoute>} />
            <Route path="overview" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer", "reports"]}><UnifiedDashboard /></ProtectedRoute>} />

            <Route path="retail" element={<ProtectedRoute allowedRoles={["superadmin"]}><Retail /></ProtectedRoute>} />
            
            {/* User Management with nested routes */}
            <Route path="users" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><UsersLayout /></ProtectedRoute>}>
              <Route index={true} element={<UsersList />} />
              <Route path="invite" element={<InviteUser />} />
              <Route path="pending-approvals" element={<PendingApprovals />} />
            </Route>

            <Route path="commissions" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer", "reports"]}><CommissionsLayout /></ProtectedRoute>}>
              <Route index={true} element={<CommissionDashboard />} />
            </Route>
            <Route path="transactions" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer", "reports"]}><Transactions /></ProtectedRoute>} />
            <Route path="margin" element={<ProtectedRoute allowedRoles={["superadmin"]}><Margin /></ProtectedRoute>} />
            <Route path="approval" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "reports"]}><PendingApprovals /></ProtectedRoute>} />

            {/* Legacy SuperDistributor routes */}
            <Route path="superdistributor" element={<ProtectedRoute allowedRoles={["superdistributor", "superadmin"]}><SuperDistributor /></ProtectedRoute>}>
              <Route index={true} element={<GetSuperDistributor />} />
              <Route path="getSuperdistributor/:id" element={<SuperDistributorDetails />} />
              <Route path="addSuperDistributor" element={<AddSuperDistributor />} />
              <Route path="superDistributorMargin" element={<SuperDistributorMargin />} />
            </Route>

            {/* Legacy Distributor routes */}
            <Route path="distributor" element={<ProtectedRoute allowedRoles={["distributor", "superadmin","superdistributor"]}><Distributor /></ProtectedRoute>}>
              <Route index={true} element={<GetDistributor />} />
              <Route path="getDistributor/:id" element={<DistributorDetails />} />
              <Route path="addDistributor" element={<AddDistributor />} />
              <Route path="distributorMargin" element={<DistributorMargin />} />
            </Route>

            {/* Legacy Retailer routes */}
            <Route path="retailer" element={<ProtectedRoute allowedRoles={["distributor", "superadmin","superdistributor"]}><Retailer /></ProtectedRoute>}>
              <Route index={true} element={<GetRetailers />} />
              <Route path="getRetailer/:id" element={<RetailerDetails />} />
              <Route path="addRetailer" element={<ProtectedRoute allowedRoles={["distributor"]}><AddRetailer /></ProtectedRoute>}/>
              <Route path="retailerMargin" element={<RetailerMargin />} />
            </Route>

            {/* Reports & Financial Views */}
            <Route path="reports" element={<ProtectedRoute allowedRoles={['superadmin', 'retailer', 'distributor', 'superdistributor', 'masterdistributor', 'reports']}><Reports/></ProtectedRoute>}>
              <Route index={true} element={<ReportsHub />}/>
              <Route path="banktransfer" element={<BankTransfer/>}/>
              <Route path="payments" element={<Payments/>}/>
              <Route path="passbook" element={<PassBook/>}/>
              <Route path="my-disputes" element={<MyDisputesTab/>}/>
              <Route path="addnewbankaccount" element={<AddNewBankAccount/>}/>
              <Route path="addcreditcard" element={<AddCreditCard/>} />
              <Route path="comingsoon" element={<ComingSoon/>} />
              <Route path="TransactionHistory" element={<TransactionHistory/>} />
            </Route>

            {/* Reporter routes */}
            <Route path="reporter" element={<ProtectedRoute allowedRoles={['superadmin','superdistributor','masterdistributor','distributor','retailer','reports','reporter']}></ProtectedRoute>}>
              <Route index={true} element={<GetReporter/>}/>
              <Route path="addReporter" element={<AddReporter/>}/>
            </Route>

            <Route path="settings" element={<ProtectedRoute allowedRoles={["distributor", "superadmin", "superdistributor", "masterdistributor", "retailer"]}><Settings /></ProtectedRoute>} />
            <Route path="paymentgateways" element={<ProtectedRoute allowedRoles={["superadmin"]}><PaymentGateWays /></ProtectedRoute>} />
            <Route path="admin/card-to-bank-config" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminCardToBankConfig /></ProtectedRoute>} />
            <Route path="card-to-bank" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><CardToBankPayment /></ProtectedRoute>} />
            <Route path="menu-access" element={<ProtectedRoute allowedRoles={["superadmin"]}><MenuAccess /></ProtectedRoute>} />
            <Route path="admin-permissions/menu-access" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminPermissionsMenuAccess /></ProtectedRoute>} />
            <Route path="admin-permissions/commission-withdrawals" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminCommissionWithdrawals /></ProtectedRoute>} />
            <Route path="admin/bbps-settings" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminBbpsSettings /></ProtectedRoute>} />
            <Route path="admin-permissions/bbps-settings" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminBbpsSettings /></ProtectedRoute>} />
            <Route path="permissions" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminPermissionsMenuAccess /></ProtectedRoute>} />
            
            {/* POS Terminal & Admin Routes */}
            <Route path="pos-payment" element={<ProtectedRoute allowedRoles={["superadmin", "admin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><PosPayment /></ProtectedRoute>} />
            <Route path="pos-approvals" element={<ProtectedRoute allowedRoles={["superadmin", "admin", "operations", "reports"]}><AdminPosApprovals /></ProtectedRoute>} />
            <Route path="pos-configurations" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><AdminPosConfig /></ProtectedRoute>} />
            <Route path="admin/pos-approvals" element={<ProtectedRoute allowedRoles={["superadmin", "admin", "operations", "reports"]}><AdminPosApprovals /></ProtectedRoute>} />
            <Route path="admin/pos-configurations" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><AdminPosConfig /></ProtectedRoute>} />

            {/* QR PG Routes */}
            <Route path="qr-pg" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><QrPgPayment /></ProtectedRoute>} />
            <Route path="admin/qr-pg/setup" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminQrPgSetup /></ProtectedRoute>} />
            <Route path="admin/qr-pg/approvals" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminQrPgSetup defaultTab="approvals" /></ProtectedRoute>} />
            <Route path="admin/qr-pg/reconciliation" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminQrPgSetup defaultTab="reconciliation" /></ProtectedRoute>} />
            
            {/* Operational Modules */}
            <Route path="profile" element={<ProtectedRoute allowedRoles={["distributor", "superadmin", "retailer", "superdistributor", "masterdistributor"]}><Profile /></ProtectedRoute>} />
            <Route path="register" element={<ProtectedRoute allowedRoles={["distributor", "superadmin", "superdistributor", "masterdistributor"]}><Register /></ProtectedRoute>} />
            <Route path="addbalance" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "distributor", "superdistributor", "masterdistributor", "reports"]}><QrPgPayment /></ProtectedRoute>} />
            <Route path="BillPayments" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "distributor", "superdistributor", "masterdistributor", "reports"]}><BillPayments/></ProtectedRoute>} />
            <Route path="billpayments" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "distributor", "superdistributor", "masterdistributor", "reports"]}><BillPayments/></ProtectedRoute>} />
            <Route path="banktransfer" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "reports"]}><BankTransfer/></ProtectedRoute>} />
            <Route path="addnewbankaccount" element={<ProtectedRoute allowedRoles={["superadmin", "retailer"]}><AddNewBankAccount/></ProtectedRoute>} />
            <Route path="payments" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "distributor", "superdistributor", "masterdistributor", "reports"]}><Payments/></ProtectedRoute>} />
            <Route path="addcreditcard" element={<ProtectedRoute allowedRoles={["superadmin", "retailer"]}><Payments/></ProtectedRoute>} />
            <Route path="comingsoon" element={<ProtectedRoute allowedRoles={["superadmin", "retailer"]}><Payments/></ProtectedRoute>} />
            <Route path="transactionhistory" element={<ProtectedRoute allowedRoles={["superadmin", "retailer"]}><Payments/></ProtectedRoute>} />
            <Route path="payin" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><Payin /></ProtectedRoute>} />
            <Route path="payment-status" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><PaymentStatus/></ProtectedRoute>} />
            <Route path="settlement" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "retailer"]}><BankSettlement /></ProtectedRoute>} />
            <Route path="admin/settlement-charges" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminSettlementCharges /></ProtectedRoute>} />
            <Route path="admin/settlement-reports" element={<Navigate to="/dashboard/admin/all-reports?tab=settlement" replace />} />
            <Route path="admin/all-reports" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminAllReports /></ProtectedRoute>} />
            <Route path="notifications" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminNotifications /></ProtectedRoute>} />
            <Route path="admin/notifications" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminNotifications /></ProtectedRoute>} />
            <Route path="admin/disputes" element={<ProtectedRoute allowedRoles={["superadmin", "operations", "reports"]}><AdminDisputeDesk /></ProtectedRoute>} />
            <Route path="reports/my-disputes" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "distributor", "superdistributor", "masterdistributor"]}><MyDisputesTab /></ProtectedRoute>} />
            <Route path="disputes" element={<ProtectedRoute allowedRoles={["superadmin", "retailer", "distributor", "superdistributor", "masterdistributor"]}><MyDisputesTab /></ProtectedRoute>} />

            {/* Notification Route Aliases & Deep Links */}
            <Route path="PendingApprovals" element={<ProtectedRoute allowedRoles={["superadmin", "superdistributor", "masterdistributor", "distributor", "reports"]}><PendingApprovals /></ProtectedRoute>} />
            <Route path="admin-transactions" element={<ProtectedRoute allowedRoles={["superadmin"]}><AdminQrPgSetup defaultTab="approvals" /></ProtectedRoute>} />
            <Route path="Passbook" element={<Navigate to="/dashboard/reports/passbook" replace />} />
            <Route path="passbook" element={<Navigate to="/dashboard/reports/passbook" replace />} />
            <Route path="qr-transactions" element={<Navigate to="/dashboard/qr-pg" replace />} />
          </Route>

          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/user" element={<ProtectedRoute allowedRoles={["distributor","superdistributor"]} ><Userpage /></ProtectedRoute>} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/refundpolicy" element={<RefundPolicy />} />
          <Route path="/payment/callback" element={<PaymentStatus />} />
          <Route path="/payment-callback" element={<PaymentStatus />} />
          <Route path="/contactus" element={<ContactUs/>} />
          <Route path="/aboutus" element={<AboutUs/>} />
          <Route path="/footer" element={<Footer/>} />
          <Route path="/features" element={<Features/>} />
          <Route path="/howitworks" element={<HowItWorks/>} />
          <Route path="/forgotpassword" element={<ForgotPassword/>} />
          <Route path="/emailotp/:id" element={<OTP/>} />
          <Route path="/passwordsetup/:id" element={<PasswordSetup/>} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </ConfigProvider>
  );
}

export default App;
